import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getAdminAnalyticsApiCache,
  getAdminAnalyticsContent,
  getAdminAnalyticsEngagement,
  getAdminAnalyticsGear,
  getAdminAnalyticsMapSpots,
  getAdminAnalyticsOverview,
  getAdminAnalyticsSocial,
  getAdminAnalyticsSurveysFeedback,
  getAdminAnalyticsSystem,
  getAdminAnalyticsUsers,
} from "../api/adminAnalytics";
import AdminAnalyticsShell from "../components/adminAnalytics/AdminAnalyticsShell";
import { aa } from "../components/adminAnalytics/adminAnalyticsUtils";
import AdminAnalyticsApiCacheTab from "../components/adminAnalytics/tabs/AdminAnalyticsApiCacheTab";
import AdminAnalyticsContentTab from "../components/adminAnalytics/tabs/AdminAnalyticsContentTab";
import AdminAnalyticsEngagementTab from "../components/adminAnalytics/tabs/AdminAnalyticsEngagementTab";
import AdminAnalyticsGearTab from "../components/adminAnalytics/tabs/AdminAnalyticsGearTab";
import AdminAnalyticsMapSpotsTab from "../components/adminAnalytics/tabs/AdminAnalyticsMapSpotsTab";
import AdminAnalyticsOverviewTab from "../components/adminAnalytics/tabs/AdminAnalyticsOverviewTab";
import AdminAnalyticsSocialTab from "../components/adminAnalytics/tabs/AdminAnalyticsSocialTab";
import AdminAnalyticsSurveysFeedbackTab from "../components/adminAnalytics/tabs/AdminAnalyticsSurveysFeedbackTab";
import AdminAnalyticsSystemTab from "../components/adminAnalytics/tabs/AdminAnalyticsSystemTab";
import AdminAnalyticsUsersTab from "../components/adminAnalytics/tabs/AdminAnalyticsUsersTab";
import "../styles/pages/AdminAnalytics.css";

const INITIAL_DAYS = 7;

const TABS = [
  {
    id: "overview",
    label: aa("tab_overview", "Overview"),
    load: getAdminAnalyticsOverview,
    component: AdminAnalyticsOverviewTab,
  },
  {
    id: "users",
    label: aa("tab_users", "Users"),
    load: getAdminAnalyticsUsers,
    component: AdminAnalyticsUsersTab,
  },
  {
    id: "engagement",
    label: aa("tab_engagement", "Engagement"),
    load: getAdminAnalyticsEngagement,
    component: AdminAnalyticsEngagementTab,
  },
  {
    id: "content",
    label: aa("tab_content", "Content"),
    load: getAdminAnalyticsContent,
    component: AdminAnalyticsContentTab,
  },
  {
    id: "social",
    label: aa("tab_social", "Social"),
    load: getAdminAnalyticsSocial,
    component: AdminAnalyticsSocialTab,
  },
  {
    id: "map-spots",
    label: aa("tab_map_spots", "Map & Spots"),
    load: getAdminAnalyticsMapSpots,
    component: AdminAnalyticsMapSpotsTab,
  },
  {
    id: "gear",
    label: aa("tab_gear", "Gear"),
    load: getAdminAnalyticsGear,
    component: AdminAnalyticsGearTab,
  },
  {
    id: "surveys-feedback",
    label: aa("tab_surveys_feedback", "Surveys & Feedback"),
    load: getAdminAnalyticsSurveysFeedback,
    component: AdminAnalyticsSurveysFeedbackTab,
  },
  {
    id: "api-cache",
    label: aa("tab_api_cache", "API & Cache"),
    load: getAdminAnalyticsApiCache,
    component: AdminAnalyticsApiCacheTab,
  },
  {
    id: "system",
    label: aa("tab_system", "System"),
    load: getAdminAnalyticsSystem,
    component: AdminAnalyticsSystemTab,
  },
];

function getCacheKey(tabId, days) {
  return `${tabId}:${days}`;
}

function getTab(tabId) {
  return TABS.find((tab) => tab.id === tabId) || TABS[0];
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(INITIAL_DAYS);
  const [activeTabId, setActiveTabId] = useState(TABS[0].id);
  const [tabStateByKey, setTabStateByKey] = useState({});
  const tabStateRef = useRef({});
  const inFlightKeysRef = useRef(new Set());

  const activeTab = useMemo(() => getTab(activeTabId), [activeTabId]);
  const activeCacheKey = getCacheKey(activeTab.id, days);
  const activeState = tabStateByKey[activeCacheKey] || {
    data: null,
    error: null,
    isLoading: true,
  };

  const setCachedTabState = useCallback((updater) => {
    setTabStateByKey((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      tabStateRef.current = next;
      return next;
    });
  }, []);

  const loadTab = useCallback(async (tabId, selectedDays, { signal, force = false } = {}) => {
    const tab = getTab(tabId);
    const cacheKey = getCacheKey(tab.id, selectedDays);
    const currentState = tabStateRef.current[cacheKey];

    if (
      !force &&
      (inFlightKeysRef.current.has(cacheKey) ||
        currentState?.data ||
        currentState?.error ||
        currentState?.isLoading)
    ) {
      return;
    }

    inFlightKeysRef.current.add(cacheKey);
    setCachedTabState((current) => ({
      ...current,
      [cacheKey]: {
        data: force ? null : current[cacheKey]?.data || null,
        error: null,
        isLoading: true,
      },
    }));

    try {
      const payload = await tab.load(selectedDays, { signal });
      if (signal?.aborted) return;
      setCachedTabState((current) => ({
        ...current,
        [cacheKey]: {
          data: payload || {},
          error: null,
          isLoading: false,
        },
      }));
    } catch (requestError) {
      if (signal?.aborted || requestError?.name === "AbortError") {
        setCachedTabState((current) => ({
          ...current,
          [cacheKey]: {
            data: current[cacheKey]?.data || null,
            error: null,
            isLoading: false,
          },
        }));
        return;
      }
      setCachedTabState((current) => ({
        ...current,
        [cacheKey]: {
          data: null,
          error: requestError,
          isLoading: false,
        },
      }));
    } finally {
      inFlightKeysRef.current.delete(cacheKey);
    }
  }, [setCachedTabState]);

  useEffect(() => {
    const currentState = tabStateRef.current[activeCacheKey];
    if (
      currentState?.data ||
      currentState?.error ||
      currentState?.isLoading ||
      inFlightKeysRef.current.has(activeCacheKey)
    ) {
      return undefined;
    }

    const controller = new AbortController();
    loadTab(activeTab.id, days, { signal: controller.signal });
    return () => controller.abort();
  }, [activeCacheKey, activeTab.id, days, loadTab]);

  const handleRetry = useCallback(() => {
    loadTab(activeTab.id, days, { force: true });
  }, [activeTab.id, days, loadTab]);

  const ActiveTabComponent = activeTab.component;

  return (
    <AdminAnalyticsShell
      days={days}
      onDaysChange={setDays}
      tabs={TABS}
      activeTabId={activeTab.id}
      onTabChange={setActiveTabId}
      tabState={activeState}
      onRetry={handleRetry}
    >
      <ActiveTabComponent data={activeState.data || {}} />
    </AdminAnalyticsShell>
  );
}
